import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { notificationService } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { guardContentSubmission, showModerationWarning } from '../../utils/moderation';
import { showToast } from '../../utils/toast';
import './CircleEventCreate.css';

const EMPTY_FORM = {
  title: '',
  mountain: '',
  date: '',
  meetingPoint: '',
  capacity: '',
  fee: '',
  course: '',
  requiredGear: '',
  description: '',
};

function CircleEventCreate() {
  const { id: circleId, eventId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const errorRef = useRef(null);

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [fieldError, setFieldError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(!!eventId);

  useEffect(() => {
    if (!eventId) return;
    let active = true;
    setLoadingEvent(true);
    getDoc(doc(db, 'recruitments', circleId, 'events', eventId))
      .then((snap) => {
        if (!active || !snap.exists()) return;
        const d = snap.data();
        setFormData({
          title: d.title || '',
          mountain: d.mountain || '',
          date: d.date || '',
          meetingPoint: d.meetingPoint || '',
          capacity: d.capacity != null ? String(d.capacity) : '',
          fee: d.fee != null ? String(d.fee) : '',
          course: d.course || '',
          requiredGear: d.requiredGear || '',
          description: d.description || '',
        });
      })
      .catch(() => showToast({ message: 'イベントデータの取得に失敗しました。', type: 'error' }))
      .finally(() => { if (active) setLoadingEvent(false); });
    return () => { active = false; };
  }, [circleId, eventId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const scrollToError = () => {
    window.setTimeout(() => {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      errorRef.current?.focus({ preventScroll: true });
    }, 0);
  };

  const validate = () => {
    if (!formData.mountain.trim()) return '山名を入力してください。';
    if (!formData.date) return '開催日を選択してください。';
    if (!formData.meetingPoint.trim()) return '集合場所を入力してください。';
    if (!formData.description.trim()) return 'イベント説明を入力してください。';
    if (formData.capacity !== '' && (isNaN(Number(formData.capacity)) || Number(formData.capacity) < 0)) {
      return '定員は0以上の整数で入力してください。';
    }
    if (formData.fee !== '' && (isNaN(Number(formData.fee)) || Number(formData.fee) < 0)) {
      return '参加費は0以上の整数で入力してください。';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) { navigate('/login'); return; }
    const validationError = validate();
    if (validationError) {
      setFieldError(validationError);
      scrollToError();
      return;
    }

    setSubmitting(true);
    setFieldError('');

    try {
      const moderation = await guardContentSubmission({
        action: 'event_create',
        contentType: 'circle_event',
        texts: [formData.title, formData.mountain, formData.meetingPoint, formData.description, formData.requiredGear],
        metadata: { circleId },
      });
      showModerationWarning(moderation);

      const eventData = {
        title: formData.title.trim() || formData.mountain.trim(),
        mountain: formData.mountain.trim(),
        date: formData.date,
        meetingPoint: formData.meetingPoint.trim(),
        capacity: formData.capacity !== '' ? Number(formData.capacity) : 0,
        fee: formData.fee !== '' ? Number(formData.fee) : 0,
        course: formData.course.trim(),
        requiredGear: formData.requiredGear.trim(),
        description: formData.description.trim(),
        organizerId: currentUser.uid,
        updatedAt: serverTimestamp(),
      };

      if (eventId) {
        await updateDoc(doc(db, 'recruitments', circleId, 'events', eventId), eventData);
        showToast({ message: 'イベントを更新しました。', type: 'success' });
        navigate(`/circles/${circleId}/events/${eventId}`);
      } else {
        const docRef = await addDoc(collection(db, 'recruitments', circleId, 'events'), {
          ...eventData,
          participantCount: 0,
          status: 'recruiting',
          createdAt: serverTimestamp(),
        });
        // EVT-06: サークルの有効メンバー（自分以外）に通知を送る
        try {
          const membersSnap = await getDocs(
            query(
              collection(db, 'recruitments', circleId, 'members'),
              where('status', '==', 'active')
            )
          );
          const eventTitle = formData.title.trim() || formData.mountain.trim();
          await Promise.all(
            membersSnap.docs
              .filter((m) => m.id !== currentUser.uid)
              .map((m) =>
                notificationService.create(m.id, 'new_circle_event', {
                  message: `新しいイベント「${eventTitle}」が作成されました。`,
                  recruitmentId: circleId,
                  targetId: docRef.id,
                  targetType: 'circle_event',
                  targetName: eventTitle,
                })
              )
          );
        } catch (notifError) {
          console.warn('Event notification failed:', notifError);
        }
        showToast({ message: 'イベントを作成しました。', type: 'success' });
        navigate(`/circles/${circleId}/events/${docRef.id}`);
      }
    } catch (error) {
      console.error('CircleEvent save failed:', error);
      setFieldError('保存に失敗しました。時間をおいて再度お試しください。');
      scrollToError();
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingEvent) return <div className="loading">読み込み中...</div>;

  return (
    <div className="cec-page">
      <div className="cec-header">
        <h1>{eventId ? 'イベントを編集' : 'イベントを作成'}</h1>
        <p>サークルのイベントを作成して参加者を募集しましょう。</p>
      </div>

      {fieldError && (
        <div ref={errorRef} className="cec-error" role="alert" aria-live="assertive" tabIndex="-1">
          {fieldError}
        </div>
      )}

      <form className="cec-form" onSubmit={handleSubmit} noValidate>
        <section className="cec-section">
          <div className="cec-section__head"><span>01</span><h2>基本情報</h2></div>
          <div className="cec-grid">
            <label className="cec-field cec-field--required">
              <span>山名</span>
              <input name="mountain" value={formData.mountain} onChange={handleChange} placeholder="例: 高尾山" maxLength={100} required />
            </label>
            <label className="cec-field cec-field--required">
              <span>開催日</span>
              <input type="date" name="date" value={formData.date} onChange={handleChange} required />
            </label>
            <label className="cec-field">
              <span>イベントタイトル</span>
              <input name="title" value={formData.title} onChange={handleChange} placeholder="例: 秋の紅葉ハイク" maxLength={120} />
            </label>
            <label className="cec-field cec-field--required">
              <span>集合場所</span>
              <input name="meetingPoint" value={formData.meetingPoint} onChange={handleChange} placeholder="例: 高尾山口駅 改札前 8:30" maxLength={200} required />
            </label>
          </div>
        </section>

        <section className="cec-section">
          <div className="cec-section__head"><span>02</span><h2>参加条件</h2></div>
          <div className="cec-grid">
            <label className="cec-field">
              <span>定員（0 = 無制限）</span>
              <input type="number" name="capacity" value={formData.capacity} onChange={handleChange} min="0" placeholder="例: 10" />
            </label>
            <label className="cec-field">
              <span>参加費（円、0 = 無料）</span>
              <input type="number" name="fee" value={formData.fee} onChange={handleChange} min="0" placeholder="例: 0" />
            </label>
            <label className="cec-field">
              <span>コース</span>
              <input name="course" value={formData.course} onChange={handleChange} placeholder="例: 1号路往復" maxLength={200} />
            </label>
            <label className="cec-field">
              <span>必要装備</span>
              <input name="requiredGear" value={formData.requiredGear} onChange={handleChange} placeholder="例: 登山靴・レインウェア・水1.5L" maxLength={300} />
            </label>
          </div>
        </section>

        <section className="cec-section">
          <div className="cec-section__head"><span>03</span><h2>説明</h2></div>
          <label className="cec-field cec-field--full cec-field--required">
            <span>イベント説明</span>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={8}
              maxLength={2000}
              placeholder="コース詳細・注意事項・持ち物・雨天時の対応などを書いてください。"
              required
            />
            <small>{formData.description.length} / 2000文字</small>
          </label>
        </section>

        <div className="cec-actions">
          <button
            type="button"
            className="cec-btn cec-btn--ghost"
            onClick={() => navigate(`/circles/${circleId}/manage`)}
            disabled={submitting}
          >
            キャンセル
          </button>
          <button type="submit" className="cec-btn cec-btn--primary" disabled={submitting}>
            {submitting ? '保存中...' : eventId ? '更新する' : 'イベントを作成'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CircleEventCreate;
